/**
 * Custom Tabs Component for Network Optimization
 * 
 * Supports two style variants:
 * 1. Default: Standard underline tabs
 * 2. Boxed: Tabs with borders and background styling (similar to the My Files page design)
 * 
 * Additional props:
 * - noPadding: Removes the default left padding
 * - alignment: Controls tab alignment ('left' | 'right')
 */
import React, {useEffect, useState} from 'react';
import {ConditionalTooltip} from '../../../../components/tooltip';

type Tab = {
    label: string;
    content: React.ReactNode;
    isDisabled?: boolean;
    disabledTooltipText?: string;
};

type TabsProps = {
    tabs: Tab[];
    onClick?: (index: number) => void;
    variant?: 'default' | 'boxed';
    noPadding?: boolean;
    alignment?: 'left' | 'right';
};

const Tabs: React.FC<TabsProps> = ({tabs, onClick, variant = 'default', noPadding = false, alignment = 'left'}) => {
    const [activeTab, setActiveTab] = useState(0);

    function setTab(index: number) {
        setActiveTab(index);
        onClick && onClick(index);
    }

    useEffect(() => {
        if (activeTab !== 0 && tabs[activeTab].isDisabled) {
            setTab(0);
        }
    }, [tabs[activeTab].isDisabled]);

    const handleTabClick = (index: number) => {
        if(!tabs[index].isDisabled) {
            setTab(index);
        }
    };

    const getButtonClasses = (index: number) => {
        if (variant === 'boxed') {
            return activeTab === index 
                ? 'px-8 py-2 cursor-pointer font-medium bg-white text-gray-900 border-t-2 border-t-sky-800 border-l border-r border-gray-200'
                : 'px-8 py-2 cursor-pointer font-normal bg-gray-200 text-gray-600 hover:text-gray-800 hover:bg-gray-200';
        }
        
        return `px-4 py-2 border-b-2 cursor-pointer ${
            activeTab === index 
                ? 'border-blue-500 font-bold text-gray-900' 
                : 'border-background-gray-200 text-gray-500 hover:text-gray-900'
        }`;
    };

    const getTabContainerClasses = () => {
        const baseClasses = `tab-buttons flex ${variant === 'boxed' ? 'gap-0 border-b border-gray-200' : 'border-b'}`;
        const paddingClass = noPadding ? '' : 'pl-6';
        const alignmentClass = alignment === 'right' ? 'justify-end' : '';
        
        return `${baseClasses} ${paddingClass} ${alignmentClass}`.trim();
    };

    return (
        <div className="tabs-component">
            <div className={getTabContainerClasses()}>
                {tabs.map((tab, index) => (
                    <ConditionalTooltip content={tab.disabledTooltipText} key={index} alignment={'bottom'} condition={!!tab.isDisabled}>
                        <button
                            key={index}
                            className={`${getButtonClasses(index)} ${activeTab === index ? 'selected' : ''}`}
                            onClick={() => handleTabClick(index)}
                            data-testid={`tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                            data-active={activeTab === index ? 'true' : 'false'}
                            style={{
                                cursor: tab.isDisabled ? 'not-allowed' : 'pointer', 
                                opacity: tab.isDisabled ? 0.6 : 1,
                                ...(variant === 'boxed' && activeTab === index ? {borderTopColor: '#38bdf8'} : {})
                            }}
                        >
                            {tab.label}
                        </button>
                    </ConditionalTooltip>
                ))}
            </div>

            <div className={`tab-content ${variant === 'boxed' ? 'border border-gray-200 border-t-0 flex w-full' : ''}`}>
                <div className='w-full'>
                    {tabs[activeTab].content}
                </div>
            </div>
        </div>
    );
};

export default Tabs;